/**
 * ============================================================================
 * 开放位置 / 招聘启事内容 - Openings Content
 * ============================================================================
 *
 * 【作用】控制“开放位置”页的标题、副标题，以及各区块：联系引导、当前/长期招聘、待遇与要求、
 *        科研方向与要求、加入收获、科研会用到的课程、Mini Tests（可选，当前已注释）、南科大 UROP/毕业设计/硕士、分组 Q&A。
 * 【如何修改】页面级改 title/subtitle；各区块改对应键下的 en/cn。新增区块在 openings 下加字段，并在 opening.jsx 中增加展示。
 *
 * 【结构说明】
 * - intro: 顶部简短引导。
 * - contactPrompt: 引导联系区块下方小字（通过导航栏「联系我们」取得联系）。
 * - currentOpenings: 当前招聘，按六类分（研究助理教授/博士后/博士/研究助理/硕士/本科和交换学生）；每类可单独注释掉以关闭该岗位展示。
 * - longTermOpenings: 长期机会说明。
 * - benefitsAndRequirements: 待遇与要求，按上述六类分别写待遇与薪资等；每类可单独注释掉，与当前招聘对应。
 * - researchDirections: 科研方向与要求，与 content-research 四个环节一致。
 * - whatYouGain: 加入后的收获。
 * - coursesUsedInResearch: 科研会用到的课程/知识（非“建议先修”，按研究环节分组）。
 * - miniTests: 博士申请可选 Mini Tests；若需展示，在下方取消注释并恢复导出，并在 opening.jsx 中取消对 miniTests 的条件判断。
 * - sustechCredits: 南科大本校 UROP、毕业设计、硕士研究说明。
 * - faqSections: 分组常见问题（招生、需要学的内容、鼓励与寄语等），每组有 sectionTitle 与 items[{q,a}]。
 */

export const openingsContent = {
  title: { en: "Openings", cn: "开放位置" },
  subtitle: {
    en: "We welcome talented researchers and students to join our group.",
    cn: "欢迎有志于跨学科研究的研究人员与学生加入我们。"
  },

  intro: {
    en: "🔬 Thank you for your interest in our group! We welcome researchers and students interested in interdisciplinary research to contact us via the Contact page or email for inquiries and discussion.",
    cn: "🔬 感谢您对我们课题组的关注！我们欢迎对跨学科研究感兴趣的研究人员与学生通过通过「联系我们」或邮件与我们联系、交流与咨询。"
  },

  /** 引导联系区块下方小字：提示通过导航栏「联系我们」取得联系。 */
  contactPrompt: {
    en: '📧 Please contact us via the "Contact Us" page or email: boyuqie@outlook.com with your application materials (CV and any required supporting documents—see position details), and include "your name + position applying for" in the subject line. Now we are actively developing multiple research directions, including chemical synthesis, condensed matter characterization, theory & computation, and AI.',
    cn: '📧 请通过「联系我们」或邮件与我们联系，并发送相关申请材料（个人简历及其他补充文件，详见各岗位要求）至boyuqie@outlook.com，并在邮件标题中注明“姓名+申请职位”。目前课题组的多个研究方向均已陆续展开，包括化学合成、凝聚态表征、理论与计算及 AI 等。'
  },

  /**
   * 当前招聘：六类岗位，每类可单独注释掉（删除或注释该键）以不展示该岗位。
   * 内容可参考南科大化学系标准与要求等；具体薪资以学校/学院最新为准，此处为示例表述。
   */
  currentOpenings: {
    title: { en: "🧑‍🔬 Current Openings", cn: "🧑‍🔬 当前开放岗位" },
    positions: {
      /* assistantProfessor: {
        label: { en: "Research Assistant Professor", cn: "研究助理教授" },
        body: {
          en: "We welcome applications from outstanding researchers with a strong track record in chemistry, physics, or related fields. The position involves independent and collaborative research within our closed-loop framework, with opportunities for grant writing and student mentoring. Contract and start date are negotiable.",
          cn: "欢迎在化学、物理及相关领域有突出成果的研究人员申请。岗位将参与课题组闭环框架下的独立与协作研究，并有机会申请项目、指导学生。聘期与到岗时间可议。"
        }
      },*/
      postdoc: {
        label: { en: "Postdoctoral Researchers", cn: "博士后" },
        body: {
          en: `🧪 We welcome scholars who have obtained a PhD in chemistry, physics, materials science, electronics, computer science, or a related field to join our research group. According to your background and interests, you will be involved in research related to synthesis, characterization, theory & computation, and/or AI. We especially look forward to young researchers willing to collaborate across disciplines and work with group members from diverse backgrounds.
🎉 If you are interested in a postdoctoral position, please send the following TWO PDF files to boyuqie@outlook.com, and include "your name + postdoc application" in the email subject:
1. Curriculum vitae;
2. Research statement (including your research experience, current scientific interests, and future plans, about one page, two or three paragraphs is sufficient);
Candidates shortlisted after initial screening will be contacted for interview as soon as possible. After entering the next stage, you will be asked to provide 2–3 letters of recommendation.`,
          cn: `🧪 我们欢迎已取得化学、物理、材料、电子、计算机等相关专业博士学位的学者申请加入课题组。您将根据自身研究背景和兴趣，参与合成、表征、理论与计算和/或 AI 等相关方向的研究。我们尤其期待愿意进入交叉领域、并与不同学科成员协作开展研究的青年学者加入。
🎉 若您对博士后岗位感兴趣，请将以下两份PDF文件发送至boyuqie@outlook.com，并在邮件标题中注明“姓名+申请博士后”：
1. 个人简历；
2. 研究陈述（包括研究经历、研究兴趣、未来研究计划等，约一页，两至三段即可）；
若通过初步筛选，我们会尽快与您联系并安排后续面试。进入下一阶段后，需要您提供2-3封推荐信。`
        }
      },
      phd: {
        label: { en: "PhD and Master Students", cn: "博士和硕士研究生" },
        body: {
          en: `🎓 We are currently accepting applications for Master's students starting Fall 2026; there are no available PhD positions for Fall 2026. If you are interested in future admissions (Fall 2027 or later), you are very welcome to reach out and inquire. If you would like to join us in Fall 2026 or Spring 2027, you may also apply for a research assistant position—please refer to the research assistant description for details on benefits, requirements, and research projects.
🎉 If you are interested in pursuing a PhD or Master's degree, please send the following three PDF files to boyuqie@outlook.com: 1. your CV; 2. a research statement (including your research experience, interests, and future plans—in about a page, two to three paragraphs); 3. your transcript. Please include "your name + applying for + PhD/Master" in the email subject line. Candidates on shortlist will be notified as soon as possible. The next steps include an interview, and if you are applying for the PhD, you will need to provide 1-2 recommendation letters (these are not required for the Master's application).`,
          cn: `🎓 我们目前开放 2026 秋的硕士名额，暂不开放 2026 秋博士名额。欢迎对之后轮次（2027 秋及以后）感兴趣的同学咨询。若您仍希望在2026年秋季/2027年春季加入我们团队，可以申请为研究助理职位，具体待遇/福利/要求，以及科研课题请参考研究助理岗位描述。
🎉 若您对就读博士与硕士学位感兴趣，请将：1. 个人简历；2. 研究陈述（包括研究经历、研究兴趣、未来研究计划等，一页纸左右，两至三段话）; 3. 成绩单的三份pdf文件，发送至boyuqie@outlook.com，并在邮件标题中注明“姓名+申请博士/硕士”。若通过初步筛选，我们会尽快通知您，后续会安排面试，如果是博士学位则需要您提供1-2封推荐信（硕士不需要）。`
        }
      },
      researchAssistant: {
        label: { en: "Research Assistants", cn: "研究助理" },
        body: {
          en: `💻 We are recruiting full-time research assistants long-term, targeting recent graduates or gap year students. According to your interests and background, you will participate in research projects on synthesis, characterization, theory & computation and/or AI. As long as you are interested in interdisciplinary research and willing to keep learning in new problems and methods, you are welcome to apply.
🎉 If you are interested in the research assistant position, please send the following TWO PDF files to boyuqie@outlook.com, and include "your name + research assistant application" in the email subject:
1. Curriculum vitae;
2. Research statement (including research experience, scientific interests, and future research plan, about one page, two or three paragraphs is sufficient);
Candidates shortlisted after initial screening will be contacted and interviewed as soon as possible. If proceeding to the next stage, you will be asked for 1–2 letters of recommendation.`,
          cn: `💻 我们长期开放面向应届毕业生或gap year同学的全职研究助理岗位，您将根据自身背景研究兴趣，参与合成、表征、理论与计算和/或 AI 等相关方向的研究工作。只要您对交叉研究有兴趣，并愿意在新的问题和方法中不断学习，我们都非常欢迎。
🎉 若您对担任研究助理感兴趣，请将以下两份PDF文件发送至boyuqie@outlook.com，并在邮件标题中注明“姓名+申请研究助理”：
1. 个人简历；
2. 研究陈述（包括研究经历、研究兴趣、未来研究计划等，约一页，两至三段即可）；
若通过初步筛选，我们会尽快与您联系并安排后续面试。进入下一阶段后，需要您提供1-2封推荐信。`
        }
      }
    }
  },

  longTermOpenings: {
    title: { en: "🔭 Long-term openings", cn: "🔭 长期机会" },
    body: {
      en: `We are dedicated to fostering better education in the AI era and training the next generation of scientists. We especially welcome undergraduates to get involved in our research projects! If you are interested in our research directions, please reach out to learn more about available opportunities—we will do our best to support you.`,
      cn: "我们致力于在 AI 时代推动更好的教育，训练新一代科研人才，特别欢迎本科生参与我们的研究项目！对我们的研究方向感兴趣的本科生，请联系我们了解具体机会，我们会尽力为您提供机会。"
    }
  },

  benefitsAndRequirements: {
    title: { en: "💼 Benefits and requirements", cn: "💼 待遇与要求" },
    byCategory: {
      /*assistantProfessor: {...},*/
      postdoc: {
        label: { en: "Postdoctoral Researchers", cn: "博士后" },
        salary: {
          en: `💰 According to the SUSTech postdoctoral policies and related regulations (please feel free to contact us for details):
1. Annual salary ~335,000 RMB (including 150,000 RMB Guangdong provincial subsidy (pre-tax) and 60,000 RMB Shenzhen living subsidy (pre-tax)); exceptional candidates may apply for the Presidential Outstanding Postdoc program, with salary exceeding 500,000 RMB including subsidies. Other benefits: social insurance, meal and holiday allowance, high-temperature allowance, year-end bonus, etc. Postdocs with Shenzhen household registration can also apply for a one-time 30,000 RMB housing/living allowance (tax-free).
2. For postdocs who stay in Shenzhen for scientific research after completion and sign a ≥3-year contract with a local employer, the Shenzhen municipal government provides a 300,000 RMB start-up research fund.
3. The group can support eligible postdocs applying for the "Guangdong Overseas Young Postdoctoral Introduction Project": 600,000 RMB from the province (cannot be used with the annual 150,000 RMB provincial subsidy; can be used with the annual 60,000 RMB Shenzhen subsidy as per city policy); postdocs who receive this and stay 3+ years in Guangdong get a further 400,000 RMB housing subsidy from the province.`,
          cn: `💰 按照南方科技大学博士后管理办法和有关规定执行，更多详情请直接咨询。

1. 年薪33.5万元（含广东省补助15万元（税前）及深圳市生活补助6万元（税前））。特别优秀者可申请校长卓越博士后，年薪可达50万元以上（含广东省和深圳市补助）。另外享有五险一金、餐补、过节费、高温补贴、年终奖等福利。 对于落户深圳的博士后，可以按博士落户的身份向深圳市申请一次性租房和生活补贴3万元（免税）。

2. 市政府对博士后出站后选择留（来）深圳从事科研工作，且与本市企事业单位签订3年以上劳动（聘用）合同的出站博士后人员，可以申请深圳市博士后留深来深科研资助。深圳市政府给予每人30万元的科研启动经费资助。

3. 课题组可协助符合条件的博士后申请“广东省海外青年博士后引进项目”。省财政给予每名博士后资助60万元生活补贴（与广东省每年15万生活补助不同时享受，与深圳市每年6万元生活补助同时享受，以深圳市规定为准）；对获得本项目资助，出站后与广东省单位签订工作协议或劳动合同，承诺连续在粤工作3年以上的博士后，省财政给予每人40万元住房补贴。`
        },
        benefits: {
          en: `⭐ 1. The group offers ample research resources and an excellent working environment, and will assist postdocs in applying as project PIs for funding from China Postdoc Science Foundation, NSFC, Guangdong or Shenzhen.
2. For outstanding postdocs, we will actively support career development, with recommendations to Research Assistant Professor at SUSTech or other universities, research institutes or enterprises, as well as further research at renowned institutions in China or abroad.
3. We support participation at academic conferences home and abroad, encourage collaboration with SUSTech or outside groups, and strive to cultivate an open, dynamic research atmosphere.
4. For new skills/knowledge needed in research, we emphasize learning together and continuous growth, and value independent learning and innovation. While assuring scientific productivity, we will do our best to balance workload and rest, so that members can balance study, research, and life, and pursue their genuine research interests in depth.`,
          cn: `⭐ 1. 课题组提供充足的科研支持和优良的工作环境，并协助博士后以负责人身份申请中国博士后科学基金、国家自然科学基金及广东省、深圳市各级科研项目。

2. 对于优秀的出站博士后，我们会积极支持其职业发展，推荐并协助申请南方科技大学研究助理教授岗位，或其他高校、科研机构及公司岗位，也支持前往国内外知名院校继续深造。

3. 我们支持参加国内外学术会议，鼓励与校内外课题组开展合作交流，努力营造开放、活跃的学术合作环境。

4. 对于科研中需要掌握的新技能和新知识，我们强调共同学习与持续成长，也重视自主学习能力和创新思维的培养。同时，我们希望在保证科研投入的基础上，尽量兼顾工作节奏与休息时间，使成员能够平衡学习、科研与生活，并持续深入自己真正感兴趣的研究方向。`
        },
        requirements: {
          en: `🧑PhD in chemistry, physics, materials, electronics, computer science, or related field; strong interest in interdisciplinary research; good communication and teamwork; strong English reading and writing ability. Due to the highly interdisciplinary nature of the group, we especially expect applicants to keep an open mind for learning, be willing to explore new fields, and grow together in collaboration.`,
          cn: `🧑‍🔬化学、物理、材料、电子、计算机等相关专业博士；对跨学科研究有强烈兴趣；具备良好的沟通与团队协作能力；有较强的英语阅读与写作能力。由于课题组研究具有鲜明的交叉学科特点，我们尤其期待申请者保持开放的学习心态，愿意接触新的研究方向，并在合作中共同成长。`
        }
      },
      phd: {
        label: { en: "PhD and Master Students", cn: "博士和硕士研究生" },
        salary: {
          en: `💰 PhD and Master’s stipends and scholarships are provided according to SUSTech graduate policies; on-campus accommodation or allowance and medical insurance are included. Please refer to official university and department information for details.`,
          cn: "💰 博士与硕士均按南科大研究生政策发放助研津贴/奖学金；提供校内住宿或补贴、医疗保险。具体见学校招生与系网说明。"
        },
        benefits: {
          en: `⭐ 1. We provide strong research support and an excellent working environment, with tailored training and guidance to support your interests, background and long-term growth in science and future career.
2. For outstanding Master’s and PhD graduates, we will fully support your applications for top PhD/postdoc positions at home and abroad, or help you pursue positions not limited to companies, government, or education.
3. We encourage you to attend conferences and engage in collaborations both within and outside SUSTech, ensuring plenty of opportunities for academic exchange and cooperation.
4. Depending on circumstances, additional research group stipends and scholarship/project recommendations are available. We respect your work-life balance and vacations so you can pursue your research passions in a sustainable way.`,
          cn: `⭐ 1. 我们提供充足的科研支持和优良的工作环境，根据您的兴趣与背景，为您提供相应的科研指导与培训，帮助您在科研和未来职业道路上不断进步。 
          2. 对于优秀的硕士与博士毕业生，我们将努力帮您推动职业发展，积极推荐并协助您申请国内外著名院校博士/博士后继续深造，或尽力协助您去企业/政府部门/学校等单位工作。
          3. 我们支持您参加学术会议，鼓励与校内外课题组合作交流，保证良好的学术交流与合作环境。
          4. 我们会根据实际情况，为您提供课题组补贴，并且推荐您申请其他奖学金/项目；同时保证学习时长与科研假期，确保您有足够的时间平衡学习、科研与生活，以及支持您追寻与深耕感兴趣的科研方向。`
        },
        requirements: {
          en: `🧑‍🔬 Bachelor’s or Master’s degree; enthusiasm for organic chemistry, quantum materials, condensed matter physics, nanodevices, or applying AI in science; with English reading and writing ability. There are no strict minimums for GPA or publications.`,
          cn: "🧑‍🔬 本科或硕士学历；对有机合成，量子材料，凝聚态物理，微纳器件，人工智能在科学中的应用有浓厚兴趣；具备阅读文献与写作的英语能力。不设最低 GPA 或论文要求。"
        }
      },
      researchAssistant: {
        label: { en: "Research Assistants", cn: "研究助理" },
        salary: {
          en: `💰 According to SUSTech regulations and personal experience, the group will provide competitive salaries and research conditions; full social insurance and SUSTech benefits are included. Selection is based on research experience, and the details can be negotiated based on your experience and job content, reference range: 90,000–150,000 RMB/year.`,
          cn: `💰根据南方科技大学相关规定以及个人工作经验，课题组将提供具有竞争力的薪酬待遇以及科研条件，并享受五险一金及南方科技大学的相关福利。岗位将根据研究经历从优录用，具体待遇可结合经验与工作内容面议，参考范围约为 9–15 万元/年。`
        },
        benefits: {
          en: `⭐ 1. The group offers strong research support and an excellent working environment, and will provide guidance and training tailored to your interests and background to continuously advance your research and future career development.
2. For excellent research assistants, we will actively support further development, recommend and assist in PhD applications at SUSTech or other renowned institutions, and help with development in enterprises, government, education, etc.
3. We support academic conference participation and collaboration with groups both within and outside our university, cultivating an open and active research environment.
4. The group will provide subsidies as appropriate, and recommend you for other scholarships or projects. At the same time, we will help balance your study arrangements and research rhythm so you can sustain growth in your area(s) of interest while balancing study, research, and life.`,
          cn: `⭐ 1. 课题组提供充足的科研支持和优良的工作环境，并结合个人兴趣与背景提供相应的科研指导与培训，帮助您在科研能力和未来职业发展上持续提升。

2. 对于表现优秀的研究助理，我们会积极支持其进一步发展，推荐并协助申请南方科技大学及其他国内外知名院校博士项目，或尽力协助前往企业、政府部门、学校等单位发展。

3. 我们支持参加学术会议，鼓励与校内外课题组合作交流，努力营造开放、活跃的学术合作环境。

4. 课题组将根据实际情况提供相应补贴，并推荐申请其他奖学金或项目。同时，也会尽量兼顾学习安排与科研节奏，帮助成员平衡学习、科研与生活，并支持其在感兴趣的方向上持续积累与深入发展。`
        },
        requirements: {
          en: `🧑‍🔬 Background in chemistry, physics, materials, electronics or computer science; strong interest in one or more areas such as organic synthesis, quantum materials, condensed matter physics, nanodevices, artificial intelligence in science. We hope you have enthusiasm for at least one main direction, are willing to learn openly, and are ready to thrive in an interdisciplinary environment. Applicants aiming for further development in related fields will be prioritized.`,
          cn: `🧑‍🔬化学、物理、材料、电子或计算机等相关背景；对有机合成，量子材料，凝聚态物理，微纳器件，人工智能在科学中的应用有浓厚兴趣。我们希望您对其中一个或多个方向有浓厚兴趣，具备开放学习的心态，愿意在跨学科环境中不断成长。有志于在相关专业继续深造者，将优先考虑。`
        }
      },
      /*
      master: {...}, */
      undergraduateExchange: {
        label: { en: "Undergraduate & Exchange Students", cn: "本科和交换学生" },
        salary: {
          en: `💰 Depending on circumstances, additional research group stipends and recommendations for scholarships/projects are available.`,
          cn: "💰 我们会根据实际情况，为您提供课题组补贴，并且推荐您申请其他奖学金/项目。"
        },
        benefits: {
          en: `⭐ 1. We will provide full research support and an excellent working environment, and we will help guide and train you according to your interests and major, so you can grow in science and your future career.
2. For outstanding undergraduates, we will do our best to help you with your applications for Master's/PhD positions at SUSTech or other top universities, or help you pursue opportunities in industry, government, or education.
3. You are encouraged to participate in academic conferences and to collaborate both within and beyond our group, with many opportunities for academic exchange.
4. We respect your study time and vacations, so you can strike a good balance between research, learning, and life. If you excel, your project can continue as an undergraduate thesis.`,
          cn: `⭐ 1. 我们提供充足的科研支持和优良的工作环境，根据您的兴趣与就读专业，为您提供相应的科研指导与培训，帮助您在科研和未来职业道路上不断进步。 
          2. 对于优秀的本科生，我们将努力帮您推动职业发展，积极推荐并协助您申请南科大以及其他国内外著名院校硕士/博士继续深造，或尽力协助您去企业/政府部门/学校等单位工作。
          3. 我们支持您参加学术会议，鼓励与校内外课题组合作交流，保证良好的学术交流与合作环境。
          4. 我们保证学习时长与科研假期，确保您有足够的时间平衡学习、科研与生活，以及支持您追寻与深耕感兴趣的科研方向。表现优秀可延续为毕业设计。`
        },
        requirements: {
          en: `🧑‍🔬 Current undergraduate or exchange student (any major); strong interest in our research topics or interdisciplinary research such as organic synthesis, quantum materials, condensed matter physics, nanodevices, or AI in science. Enthusiastic about exploring science beyond the classroom and keen to dive deeper.`,
          cn: "🧑‍🔬 在读本科生或交换生（专业不限）；对本组课题或对跨学科研究有强烈兴趣，比如有机合成，量子材料，凝聚态物理，微纳器件，人工智能在科学中的应用等。愿意在课外继续研究科研和深入学习。"
        }
      }
      // 不需要某类待遇展示时，将对应键注释或删除；此处可填写 assistantProfessor 等与 currentOpenings 对应的键。
    }
  },

  researchDirections: {
    title: { en: "🔬 Research Directions & Courses/Skills You Might Use", cn: "🔬 科研方向&可能涉及的课程/技能" },
    intro: {
      en: `🔬 Our group focuses on the design, construction, and study of organic quantum materials across four major pillars: synthesis, characterization, theory & computation, and AI-accelerated science. Listed below are relevant background courses and skill areas often encountered in our work. You only need a basic foundation in some of these—there is no requirement to master all before joining. You can specialize in a particular pillar of your choice, or join interdisciplinary collaborations as you prefer. We encourage ongoing learning in research and expect everyone to collaborate actively while developing their own specialization.`,
      cn: `🔬 课题组围绕合成、表征、理论与计算、AI 辅助开发四个环节，开展有机量子材料的设计、构筑与研究。以下列出的是课题组研究中常涉及的课程背景与技能方向。申请者具备一部分基础即可，不要求在入组前全部掌握。具体内容会在研究过程中逐步深化，你可以根据自身兴趣侧重某一环节，也可以尝试跨环节合作。我们鼓励在科研实践中持续学习，同时希望大家在发展自身专长的基础上，与其他方向成员保持紧密协作。`
    },
    pillars: [
      {
        name: { en: "Chemical Synthesis", cn: "化学合成" },
        desc: { 
          en: `🧪 Basic foundations generally include courses such as organic chemistry, inorganic chemistry, quantum chemistry, materials chemistry and surface chemistry, plus further advanced training. Research may involve organic/inorganic synthesis, framework chemistry (e.g. MOF/COF/HOF) molecular design and growth, plus exposure to basic theory, coding, and AI tools. This direction tightly collaborates with characterization, computational theory, and AI.`,
          cn: `🧪 相关基础通常包括有机化学、无机化学、量子化学、材料化学与表面化学等课程，以及其进一步的高级课程训练。研究中可能涉及有机合成、无机合成、框架化学（如 MOF / COF / HOF 等）的分子设计与生长，也可能接触初步的理论计算、编程和 AI 工具。该方向通常与表征、理论计算及 AI 环节密切协作。`
        }
      },
      {
        name: { en: "Condensed-Matter Characterization", cn: "凝聚态表征" },
        desc: { 
          en: `🔎 Relevant background typically includes quantum mechanics, solid state physics and advanced courses. Projects may involve UHV surface reaction techniques, low-temperature scanning probe microscopy (LT-SPM), low-dimensional device fabrication, plus elements of theoretical computation, coding, and AI. This direction collaborates closely with synthesis, computation/AI.`,
          cn: `🔎 相关基础通常包括量子力学、固体物理等课程，以及其进一步的高级课程训练。研究中可能涉及超高真空表面反应技术、低温扫描探针显微技术（LT-SPM）操作、低维器件制备等实验方法，并结合一定的理论计算、编程和 AI 工具使用。该方向通常与合成、理论计算及 AI 环节密切协作。`
        }
      },
      {
        name: { en: "Theory & Computation/AI Acceleration", cn: "理论与计算/AI 辅助开发" },
        desc: { 
          en: `💻 Core background includes quantum mechanics, first-principles calculations, solid-state physics, programming languages, and machine learning/AI, plus higher-level coursework. Research may involve computational modeling and data analysis, building/using deep learning frameworks, ML force fields, generative models, or agentic AI workflows, and works synergistically with lab synthesis and characterization.`,
          cn: `💻 相关基础通常包括量子力学、第一性原理计算、固体物理、编程语言、机器学习与人工智能等课程，以及其进一步的高级课程训练。研究中可能涉及计算模拟与数据分析相关编程、深度学习框架的搭建与使用、机器学习力场、生成模型以及 agentic AI 流程等内容，并与合成和表征方向形成协同。`
        }
      },
    ]
  },

  whatYouGain: {
    title: { en: "🌟 What You Will Gain After Joining", cn: "🌟 加入后您将收获" },
    items: [
      {
        title: { en: "Sufficient and Smooth Communication", cn: "保证沟通顺畅" },
        desc: { 
          en: `🗣️ Sufficient and smooth communication: I will arrange one-on-one meetings based on actual need, generally weekly or biweekly, to discuss learning progress, experiment design, research problems and future plans. Your ideas and suggestions will be listened to carefully with timely feedback and support.`,
          cn: `🗣️充分、顺畅的交流机制：我会根据实际需要安排一对一讨论，一般为每周或每两周一次，围绕学习进展、实验设计、研究问题和未来计划展开交流。我会认真听取你的想法与建议，并及时提供反馈与支持。`
        }
      },
      {
        title: { en: "Supportive and Collaborative Environment", cn: "支持与协作的环境" },
        desc: { 
          en: `🤝 A research environment valuing support and collaboration: We hope group members collaborate as partners and colleagues, not just as traditional supervisors and students. Everyone works together on scientific questions, divides work according to strengths, and grows collaboratively.`,
          cn: `🤝支持与协作并重的研究环境：我们希望课题组成员以伙伴和同事的方式协作，而不仅仅停留在传统师生关系中。大家围绕共同的科学问题开展讨论、分工合作、共同进步。`
        }
      },
      {
        title: { en: "Develop Your Own Scientific Style & Taste", cn: "培养自己的科学风格与品味" },
        desc: { 
          en: `🔬 Gradually develop your own scientific style and taste: I will encourage and help you combine your own interests and experience to gradually develop your own sense of research problems and approaches, pursue what you are truly passionate about, and establish your own scientific style and judgement.`,
          cn: `🔬逐步形成自己的科学风格与研究品味：我会鼓励并帮助你结合自身兴趣与背景，逐步形成适合自己的科研问题意识和解决方式，探索真正感兴趣的方向，建立属于自己的科学风格与判断力。`
        }
      },
      {
        title: { en: "Comprehensive AI-Era Research Training", cn: "掌握 AI 时代的综合技能" },
        desc: { 
          en: `🤖 Develop comprehensive research abilities for the AI era: We encourage people to use AI tools in a standardized and effective way and to keep up with technological advances. Through systematic discussion and practical training, you will develop the skillset needed for research in the age of AI.`,
          cn: `🤖面向 AI 时代的综合能力训练：我们鼓励规范、有效地使用 AI 工具，并持续关注相关技术的发展。希望通过系统讨论与实际研究训练，帮助你逐步建立适应 AI 时代的综合科研能力。`
        }
      },
      {
        title: { en: "Support for Diverse Career Goals", cn: "针对不同目标的出路规划与机会" },
        desc: { 
          en: `🚀 Support for diverse career goals: Whether you lean toward academia or industry, we will support your choice and do our best to help you compete for and secure future opportunities.`,
          cn: `🚀面向不同目标的发展支持：无论你未来更倾向于学术界还是工业界，我们都支持你根据自身情况作出选择，并尽力为你争取和推荐有助于未来发展的机会。 `
        }
      },
      {
        title: { en: "Opportunities for Communication & Collaboration", cn: "交流与合作机会" },
        desc: { 
          en: `🌐 Opportunities for exchange and collaboration: We will actively provide and encourage you to pursue collaborations with researchers from different disciplines/institutions, including academia and industry. This expands your horizons and supports higher quality research.`,
          cn: `🌐交流与合作的机会：我们会积极提供并鼓励你主动争取与不同学科、不同机构研究者合作的机会，包括学术界与工业界，从而拓展视野，推动更高质量的研究工作。`
        }
      }
    ]
  },

  
  /** 科研会用到的课程/知识（按研究环节分组，非“建议先修”） 
  coursesUsedInResearch: {...}, 
  */
  

  /*
   * 【博士申请 Mini Tests】...
   */
  // miniTests: { ... }  // 此处可填写博士申请 Mini Tests 配置；需在 opening.jsx 中启用对应展示逻辑

  /** Frequently Asked Questions: Admissions, Preparation, Encouragement, etc. */
  faqSections: [
    {
      sectionTitle: { en: "📋 Application", cn: "📋 关于申请内容" },
      items: [
        { 
          q: { en: "When are PhD applications open?", cn: "博士申请何时开放？" },
          a: {
            en: "If you are interested in applying for future cycles (like Fall 2027 or after), please feel free to reach out at any time. The official schedule follows SUSTech’s graduate admissions—typically applications open each autumn. For up-to-date details, just get in touch with us.",
            cn: "欢迎对之后轮次（如 2027 秋及以后）感兴趣的同学咨询。具体时间以学校研究生招生为准，一般为每年秋季联系，开放申请，可联系我们获取最新信息。"
          }
        },
        { 
          q: { en: "Do I need a Master’s to apply for PhD?", cn: "申请博士是否必须有硕士学位？" },
          a: {
            en: "You do not need a Master’s—both Bachelor’s graduates (direct entry) and those with a Master’s are welcome to apply. Just make sure you meet the university’s requirements for that year. Feel free to ask us if you have questions about your eligibility.",
            cn: "不是必须需要。本科直博或硕士均可申请，可联系我们获取最新信息，具体以学校当年规定为准。"
          }
        },
        { 
          q: { en: "What materials should I prepare?", cn: "需要准备哪些材料？" },
          a: {
            en: "📄 Depending on the position, requirements may vary, but usually you’ll need a CV, transcripts, a research statement (explaining your interests and why you want to join our group), and sometimes reference letters. Please refer to the specific posting for details, or just ask us if you’re unsure.",
            cn: "📄 根据不同岗位要求不同，一般为个人简历加上部分相关材料（成绩单、研究陈述、推荐信等），具体请看岗位描述。"
          }
        },
        { 
          q: { en: "Is there an English requirement?", cn: "有英语要求吗？" },
          a: {
            en: "🌎 You should be comfortable reading scientific literature and writing papers in English. As for official test scores, just follow the requirements set by the university’s graduate admissions.",
            cn: "🌎 一般需要具备英语文献与撰写学术论文的基本能力；具体分数以学校研究生招生政策为准。"
          }
        },
        {
          q: { en: "What should be included in a research statement?", cn: "研究陈述需要写哪些内容？" },
          a: {
            en: "📝 We recommend a one page research statement (about two or three paragraphs) that includes your past research experience, current research interests, future research plan, and why you are interested in joining this group. The key is not length, but to clearly explain what you have done, what you wish to do, and what fits between you and the group.",
            cn: "📝研究陈述建议控制在一页左右、两至三段即可，内容可包括：过往研究经历、当前研究兴趣、未来研究计划，以及你为什么希望加入本课题组。重点不在于写得多，而在于写清楚你做过什么、想做什么，以及你与课题组研究方向的契合点。"
          }
        }
      ]
    },
    {
      sectionTitle: { en: "📚 Learning Needed", cn: "📚 需要学习的内容与掌握的技能" },
      items: [
        {
          q: { en: "Do I need to master all four areas (synthesis, characterization, theory, AI)?", cn: "是否需要掌握合成、表征、理论、AI 四个方向？" },
          a: {
            en: "No. You can focus on one direction that most interests you or explore interdisciplinary collaboration. What matters most is your commitment and potential in your primary direction, and your willingness to collaborate in group work.",
            cn: "不需要。你可以重点发展某一个方向，也可以根据兴趣跨多个环节开展工作。我们更看重你在主攻方向上的潜力与投入，以及与其他方向协作的意愿。"
          }
        },
        {
          q: { en: "I come from chemistry/physics/electronics/computer science/other backgrounds—is that OK?", cn: "我来自化学 / 物理 / 电子 / 计算机等不同背景，可以申请吗？" },
          a: {
            en: "Yes. We welcome applicants from chemistry, physics, materials, electronics, computer science or other related fields. What matters more than your undergraduate major is your research interests, learning motivation, and fit with the group’s direction.",
            cn: "可以。我们欢迎化学、物理、材料、电子、计算机及其他相关专业背景的申请者。相比具体专业出身，我们更看重你的研究兴趣、学习动力和与课题组方向的匹配度。"
          }
        },
        {
          q: { en: "Can I apply as a research assistant without prior research experience?", cn: "没有科研经历可以申请研究助理吗？" },
          a: {
            en: "🌱 Yes. For relevant positions, we do not require prior research experience or papers. We care more about your motivation, capacity to learn and think, and fit with our research.",
            cn: "🌱 可以。对于相关岗位，我们不硬性要求申请者此前必须有科研经历或论文成果。我们更关注你的动机、学习能力、思考能力以及与研究方向的契合程度。"
          }
        },
        {
          q: { en: "Are there minimum GPA or test score requirements?", cn: "有最低 GPA 或考试成绩要求吗？" },
          a: {
            en: "📊 The group does not set a minimum GPA. We evaluate based on your coursework, research statement, and background fit. Research assistant positions generally do not require test scores.",
            cn: "📊 课题组本身不预设最低 GPA 要求。我们会综合考虑课程表现、研究陈述、背景匹配度等因素。研究助理岗位一般不要求相关考试成绩。"
          }
        }
      ]
    },
    {
      sectionTitle: { en: "💡 Encouragement & Message", cn: "💡 鼓励与寄语" },
      items: [
        {
          q: { en: "What qualities do you value most in candidates?", cn: "我们最看重候选人的哪些特质？" },
          a: {
            en: "🤔 We value real interest in interdisciplinary research and openness to new directions, solid thinking and clear communication, and work style that is reliable, sincere, and collaborative.",
            cn: "🤔 我们看重申请者对交叉学科研究的真实兴趣，对新方向的开放性与投入意愿，扎实思考问题的能力，清晰的表达能力，以及可靠、真诚、愿意合作的工作方式。"
          }
        },
        {
          q: { en: "Is it possible to do an internship or short visit before applying?", cn: "申请前可以做实习或短期访问吗？" },
          a: {
            en: "🧭 When possible, we welcome short-term visits, internships, or project-based collaborations. Applicants from undergraduate to PhD can all participate. Please briefly introduce your background, goals, and preferred direction in your email.",
            cn: "🧭 在条件允许的情况下，我们欢迎短期访问、实习或项目制合作。从本科生到博士生，都有可能参与相关交流。来信时可简要介绍你的背景、目标和希望参与的方向。"
          }
        },
        {
          q: { en: "What is the group atmosphere like?", cn: "课题组氛围如何？" },
          a: {
            en: "🤗 We strive to provide a supportive, open, and collaborative group atmosphere, hoping members relate as genuine colleagues and partners. We encourage independent thinking as well as teamwork, and support both academic and industrial career directions.",
            cn: "🤗 我们努力营造支持、开放、合作的课题组氛围，希望成员之间以伙伴和同事的方式相处。我们鼓励独立思考，也重视团队合作，并支持成员根据自身情况探索学术界或工业界的发展道路。"
          }
        },
        {
          q: { en: "Any suggestions for applicants?", cn: "对申请者有什么建议？" },
          a: {
            en: "🔍 Before you apply, please familiarize yourself with the group’s research and existing work, think about problems that really interest you, which directions fit your background best, and what you want to develop in the future. Be as specific as possible about your interests, motivation, and why you chose our group in your research statement. You are welcome to email with specific questions.",
            cn: "🔍 建议在申请前先了解课题组的大致研究方向和已开展的工作，想清楚自己真正感兴趣的问题、已有背景与哪些方向更契合，以及你希望在未来进一步发展的内容。写研究陈述时，尽量把自己的兴趣、动机和选择本课题组的原因写得具体一些。若有具体问题，也欢迎来信交流。"
          }
        }
      ]
    }
  ]
};

/*{
      sectionTitle: { en: "UROP, FYP & Master at SUSTech", cn: "南科大毕业设计与硕士" },
      items: [...]
}, */

